'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X, Megaphone, ExternalLink, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getActiveAnnouncements, dismissAnnouncement, snoozeAnnouncement, type Announcement } from '@/lib/api-client';

export default function AnnouncementPopup() {
  const { user } = useAuth();
  const [popup, setPopup] = useState<Announcement | null>(null);
  const [queue, setQueue] = useState<Announcement[]>([]);
  const [busy, setBusy] = useState(false);

  const fetchAnnouncements = useCallback(() => {
    if (!user) return;
    getActiveAnnouncements()
      .then((all) => {
        const popups = all.filter((a) => a.displayType === 'popup');
        if (popups.length) {
          setPopup(popups[0]);
          setQueue(popups.slice(1));
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const advance = () => {
    if (queue.length) {
      setPopup(queue[0]);
      setQueue((q) => q.slice(1));
    } else {
      setPopup(null);
    }
  };

  // Escape key = just close (no dismiss)
  useEffect(() => {
    if (!popup) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') advance(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // X button or "Dismiss" = permanent dismiss
  const handleDismiss = async () => {
    if (!popup || busy) return;
    setBusy(true);
    try { await dismissAnnouncement(popup.id); } catch { /* ignore */ }
    setBusy(false);
    advance();
  };

  // "Remind Me Later" = snooze for configured hours
  const handleSnooze = async () => {
    if (!popup || busy) return;
    setBusy(true);
    try { await snoozeAnnouncement(popup.id); } catch { /* ignore */ }
    setBusy(false);
    advance();
  };

  // Click outside = just close without any API call (will reappear on refresh)
  const handleBackdropClick = () => {
    if (busy) return;
    advance();
  };

  if (!popup) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleBackdropClick}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-card-bg border border-default rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X = permanent dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-fg-subtle hover:text-white transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
              <Megaphone size={16} className="text-sky-400" />
            </div>
            <h2 className="text-base font-bold text-white pr-6">{popup.title}</h2>
          </div>

          {/* Body */}
          {popup.bodyType === 'image' && popup.body ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={popup.body}
              alt={popup.title}
              className="w-full max-h-72 object-contain rounded-lg mb-4"
            />
          ) : popup.body ? (
            <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed mb-4">{popup.body}</p>
          ) : null}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {popup.ctaUrl && (
              <a
                href={popup.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 px-4 py-2 rounded-lg transition-colors"
              >
                {popup.ctaLabel || 'Learn more'} <ExternalLink size={12} />
              </a>
            )}
            <button
              onClick={handleSnooze}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted bg-surface hover:bg-card-hover-bg px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <Clock size={12} />
              Remind Me Later
            </button>
            <button
              onClick={handleDismiss}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-sky-600 hover:bg-sky-500 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
